import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Mail, MessageSquare, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CampaignEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
  availableLeads: any[];
  onEnrolled?: () => void;
}

type Readiness = {
  loading: boolean;
  blocked: string[];
  needsEmail: boolean;
  needsSms: boolean;
  existingLeadIds: Set<string>;
};

const leadEmail = (lead: any) => lead.contact_email || lead.email || "";
const leadPhone = (lead: any) => lead.contact_phone || lead.phone || "";

export default function CampaignEnrollmentDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  availableLeads,
  onEnrolled
}: CampaignEnrollmentDialogProps) {
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [readiness, setReadiness] = useState<Readiness>({ loading: false, blocked: [], needsEmail: true, needsSms: false, existingLeadIds: new Set() });

  useEffect(() => {
    if (!open || !campaignId) return;
    setSelectedLeads([]);
    void loadReadiness();
  }, [open, campaignId]);

  const loadReadiness = async () => {
    setReadiness(prev => ({ ...prev, loading: true }));
    const blocked: string[] = [];
    try {
      const { data: campaign, error: campaignError } = await supabase
        .from("ai_campaigns")
        .select("status, campaign_variants(sequence_id)")
        .eq("id", campaignId)
        .single();
      if (campaignError) throw campaignError;
      if (!campaign || !["approved", "active"].includes(campaign.status)) blocked.push("Campaign must be approved before enrolling leads.");

      const sequenceId = campaign?.campaign_variants?.find((variant: any) => variant.sequence_id)?.sequence_id;
      if (!sequenceId) blocked.push("Campaign has no sequence attached.");

      let needsEmail = true;
      let needsSms = false;
      if (sequenceId) {
        const { data: steps, error: stepsError } = await supabase
          .from("sequence_steps")
          .select("step_type")
          .eq("sequence_id", sequenceId);
        if (stepsError) throw stepsError;
        if (!steps?.length) blocked.push("Campaign sequence has no steps.");
        needsEmail = !steps?.length || steps.some((step: any) => step.step_type === "email");
        needsSms = Boolean(steps?.some((step: any) => step.step_type === "sms"));
      }

      const [{ count: gmailCount }, { data: org }, { data: existing }] = await Promise.all([
        supabase.from("gmail_tokens").select("id", { count: "exact", head: true }),
        supabase.from("organizations").select("sms_enabled, sms_from_number").maybeSingle(),
        supabase.from("campaign_enrollments").select("lead_id").eq("campaign_id", campaignId),
      ]);

      if (needsEmail && !gmailCount) blocked.push("Connect Gmail in Email Hub before launching email steps.");
      if (needsSms && (!(org as any)?.sms_enabled || !(org as any)?.sms_from_number)) blocked.push("Set up SMS sending before launching text steps.");

      setReadiness({ loading: false, blocked, needsEmail, needsSms, existingLeadIds: new Set((existing || []).map((row: any) => row.lead_id)) });
    } catch (error: any) {
      setReadiness({ loading: false, blocked: [error.message || "Could not verify campaign readiness."], needsEmail: true, needsSms: false, existingLeadIds: new Set() });
    }
  };

  const eligibleLeads = useMemo(() => {
    return availableLeads.filter((lead) => {
      if (readiness.existingLeadIds.has(lead.id)) return false;
      if (readiness.needsEmail && !leadEmail(lead)) return false;
      if (readiness.needsSms && (!leadPhone(lead) || !lead.sms_consent || lead.sms_opted_out_at)) return false;
      return true;
    });
  }, [availableLeads, readiness]);

  const ineligibleCount = Math.max(0, availableLeads.length - eligibleLeads.length);

  const handleToggleLead = (leadId: string) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleToggleAll = () => {
    if (selectedLeads.length === eligibleLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(eligibleLeads.map(l => l.id));
    }
  };

  const handleEnroll = async () => {
    if (readiness.blocked.length > 0) {
      toast.error(readiness.blocked[0]);
      return;
    }
    if (selectedLeads.length === 0) {
      toast.error("Please select at least one eligible lead");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("enroll-leads-in-campaign", {
        body: { campaignId, leadIds: selectedLeads }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`${data.enrolled} lead(s) enrolled successfully`);
      onOpenChange(false);
      setSelectedLeads([]);
      onEnrolled?.();
    } catch (error: any) {
      console.error("Error enrolling leads:", error);
      toast.error(error.message || "Failed to enroll leads");
    } finally {
      setLoading(false);
    }
  };

  const blocked = readiness.blocked.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Enroll Leads in Campaign
          </DialogTitle>
          <DialogDescription>
            Select eligible leads to enroll in <strong>{campaignName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="gap-1"><Mail className="h-3 w-3" />Email {readiness.needsEmail ? "required" : "optional"}</Badge>
            <Badge variant="outline" className="gap-1"><MessageSquare className="h-3 w-3" />SMS {readiness.needsSms ? "required" : "not used"}</Badge>
            {ineligibleCount > 0 && <Badge variant="secondary">{ineligibleCount} skipped</Badge>}
          </div>

          {readiness.loading ? (
            <Alert><Loader2 className="h-4 w-4 animate-spin" /><AlertDescription>Checking launch readiness...</AlertDescription></Alert>
          ) : blocked ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc space-y-1 pl-4">
                  {readiness.blocked.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-primary/25 bg-primary/5"><CheckCircle2 className="h-4 w-4" /><AlertDescription>Campaign is ready to launch for eligible leads.</AlertDescription></Alert>
          )}

          <div className="flex items-center gap-2 border-b pb-2">
            <Checkbox
              checked={eligibleLeads.length > 0 && selectedLeads.length === eligibleLeads.length}
              onCheckedChange={handleToggleAll}
              disabled={blocked || eligibleLeads.length === 0}
            />
            <span className="text-sm font-medium">
              Select All ({selectedLeads.length}/{eligibleLeads.length})
            </span>
          </div>

          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {eligibleLeads.map(lead => (
                <div
                  key={lead.id}
                  className="flex cursor-pointer items-center gap-3 rounded border p-3 hover:bg-accent/50"
                  onClick={() => handleToggleLead(lead.id)}
                >
                  <Checkbox
                    checked={selectedLeads.includes(lead.id)}
                    onCheckedChange={() => handleToggleLead(lead.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{lead.contact_name || "Unnamed lead"}</p>
                    <p className="truncate text-sm text-muted-foreground">{leadEmail(lead) || "No email"}</p>
                    <p className="truncate text-xs text-muted-foreground">{lead.property_address}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">{lead.status}</div>
                </div>
              ))}
              {eligibleLeads.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No eligible leads available for this campaign.</div>}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleEnroll} disabled={loading || blocked || selectedLeads.length === 0}>
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enrolling...</> : <><UserPlus className="mr-2 h-4 w-4" />Enroll {selectedLeads.length} Lead{selectedLeads.length !== 1 ? "s" : ""}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
